import React, { useEffect, useState } from 'react';
import { AlertTriangle, Clock, LogOut, Sun, Moon } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  IconButton,
  useTheme,
  ThemeProvider,
  createTheme,
  LinearProgress
} from '@mui/material';
import logoKomoks from '../assets/icons/Logo_Komoks.png';
import { useAuth } from 'react-oidc-context';
import { useSelector, useDispatch } from 'react-redux';
import { toggleDarkMode } from '../store/reducers/stateReducer';

const NeuralBackground = ({ isDark }) => {
  const canvasRef = React.useRef(null);
  const mouseRef = React.useRef({ x: -100, y: -100 });
  const nodesRef = React.useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize nodes
    const nodes = [];
    const isMobile = window.innerWidth <= 768;
    for (let i = 0; i < (isMobile ? 30 : 120); i++) {
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: Math.random() * 2 - 1,
        vy: Math.random() * 2 - 1
      });
    }
    nodesRef.current = nodes;

    const animate = () => {
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = isDark ? '#000000' : '#e6ede6';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw nodes
      for (let node of nodes) {
        let dx = mouseRef.current.x - node.x;
        let dy = mouseRef.current.y - node.y;
        let distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 150) {
          node.vx += dx * 0.005;
          node.vy += dy * 0.005;
        }

        node.x += node.vx;
        node.y += node.vy;

        node.vx += (Math.random() - 0.5) * 0.1;
        node.vy += (Math.random() - 0.5) * 0.1;

        node.vx *= 0.98;
        node.vy *= 0.98;

        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

        // Красивый голубой цвет для вершин
        ctx.fillStyle = isDark ? '#4FC3F7' : '#29B6F6'; // Светло-голубой для темного режима, голубой для светлого
        ctx.beginPath();
        ctx.arc(node.x, node.y, 3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          let dx = nodes[i].x - nodes[j].x;
          let dy = nodes[i].y - nodes[j].y;
          let distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 150) {
            // Красивый темно-синий цвет для ребер
            ctx.strokeStyle = isDark ?
              `rgba(25, 118, 210, ${1 - distance / 150})` : // Темно-синий для темного режима
              `rgba(13, 71, 161, ${1 - distance / 150})`; // Более темно-синий для светлого режима
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      requestAnimationFrame(animate);
    };

    animate();

    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDark]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: -1,
        pointerEvents: 'none'
      }}
    />
  );
};

function AuthErrorPage() {
  const auth = useAuth();
  const dispatch = useDispatch();
  const darkMode = useSelector(state => state.state.darkMode);
  const [countdown, setCountdown] = useState(5);

  // Установка заголовка для страницы ошибки
  useEffect(() => {
    document.title = "Ошибка авторизации - Ситуация в КОМОКС";
  }, []);

  // Таймер обратного отсчета
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          auth.signoutRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [auth]);

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#FF9800' : '#F57C00',
      },
      secondary: {
        main: darkMode ? '#F57C00' : '#E65100',
      },
      background: {
        default: darkMode ? '#121212' : '#f5f5f5',
        paper: darkMode ? '#1e1e1e' : '#ffffff',
      },
      error: {
        main: darkMode ? '#FF9800' : '#F57C00',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    shape: {
      borderRadius: 12,
    },
  });

  const handleLogout = () => {
    auth.signoutRedirect();
  };

  const handleToggleDarkMode = () => {
    dispatch(toggleDarkMode());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          bgcolor: darkMode ? 'grey.900' : 'grey.50',
          zIndex: 10000,
          px: { xs: 1, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <NeuralBackground isDark={darkMode} />

        {/* Переключатель темного режима */}
        <IconButton
          onClick={handleToggleDarkMode}
          sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 1,
            bgcolor: darkMode ? 'grey.800' : 'background.paper',
            color: darkMode ? 'white' : 'text.primary',
            '&:hover': {
              bgcolor: darkMode ? 'grey.600' : 'grey.100',
            },
          }}
        >
          {darkMode ? <Sun size={24} /> : <Moon size={24} />}
        </IconButton>

        {/* Логотип КОМОКС в правом нижнем углу */}
         <Box
           sx={{
             position: 'fixed',
             bottom: 16,
             right: 16,
             zIndex: 1,
             display: 'flex',
             alignItems: 'center',
             gap: 1,
             borderRadius: 2,
             px: 2,
             py: 1,
             backdropFilter: 'blur(10px)',
           }}
         >
          <Typography
            variant="body1"
            color={darkMode ? 'grey.100' : 'text.primary'}
            sx={{ fontWeight: 500 }}
          >
            Ситуация в КОМОКС
          </Typography>
          <img
            src={logoKomoks}
            alt="КОМОКС"
            style={{
              width: 32,
              height: 32,
              objectFit: 'contain'
            }}
          />
        </Box>

        <Container maxWidth="md" sx={{ py: 4 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
             <Card
               sx={{
                 bgcolor: darkMode ? 'grey.800' : 'background.paper',
                 borderRadius: 3,
                 boxShadow: darkMode ?
                   '0 8px 32px rgba(255, 152, 0, 0.15)' :
                   '0 8px 32px rgba(245, 124, 0, 0.15)',
                 border: darkMode ? '1px solid rgba(255, 152, 0, 0.3)' : '1px solid rgba(245, 124, 0, 0.3)',
                 textAlign: 'center',
                 p: 4,
                 backdropFilter: 'blur(10px)',
                 transition: 'all 0.3s ease-in-out',
                 '&:hover': {
                   boxShadow: darkMode ?
                     '0 12px 40px rgba(255, 152, 0, 0.2)' :
                     '0 12px 40px rgba(245, 124, 0, 0.2)',
                   transform: 'translateY(-2px)'
                 }
               }}
             >
              <CardContent>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                   <AlertTriangle 
                     size={80} 
                     color={darkMode ? '#FF9800' : '#F57C00'}
                     style={{ marginBottom: '24px' }}
                   />
                </motion.div>

                <Typography
                  variant="h4"
                  component="h1"
                  color={darkMode ? 'grey.100' : 'text.primary'}
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                  }}
                >
                  Ошибка авторизации
                </Typography>

                <Typography
                  variant="h6"
                  color={darkMode ? 'grey.300' : 'text.secondary'}
                  sx={{
                    mb: 3,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    lineHeight: 1.6
                  }}
                >
                   Доступ ограничен
                </Typography>

                <Typography
                  variant="body1"
                  color={darkMode ? 'grey.400' : 'text.secondary'}
                  sx={{
                    mb: 4,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: 1.6,
                    maxWidth: '600px',
                    mx: 'auto'
                  }}
                >
                  Обратитесь к администратору для получения соответствующих прав доступа.
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body2"
                    color={darkMode ? 'grey.400' : 'text.secondary'}
                    sx={{ mb: 1 }}
                  >
                    Автоматический выход через:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                     <Clock size={20} color={darkMode ? '#FF9800' : '#F57C00'} />
                     <Typography
                       variant="h5"
                       color={darkMode ? '#FF9800' : '#F57C00'}
                       sx={{ fontWeight: 600 }}
                     >
                      {countdown}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={darkMode ? 'grey.400' : 'text.secondary'}
                    >
                      сек.
                    </Typography>
                  </Box>
                   <LinearProgress
                     variant="determinate"
                     value={(5 - countdown) * 20}
                     sx={{
                       mt: 1,
                       height: 6,
                       borderRadius: 3,
                       bgcolor: darkMode ? 'grey.700' : 'grey.200',
                       '& .MuiLinearProgress-bar': {
                         bgcolor: darkMode ? '#FF9800' : '#F57C00',
                         borderRadius: 3,
                         boxShadow: darkMode ? 
                           '0 0 10px rgba(255, 152, 0, 0.5)' : 
                           '0 0 10px rgba(245, 124, 0, 0.5)'
                       }
                     }}
                   />
                </Box>

                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                   <IconButton
                     onClick={handleLogout}
                     sx={{
                       bgcolor: darkMode ? '#FF9800' : '#F57C00',
                       color: 'white',
                       px: 3,
                       py: 1.5,
                       borderRadius: 2,
                       fontSize: '1rem',
                       fontWeight: 500,
                       boxShadow: darkMode ? 
                         '0 4px 20px rgba(255, 152, 0, 0.3)' : 
                         '0 4px 20px rgba(245, 124, 0, 0.3)',
                       '&:hover': {
                         bgcolor: darkMode ? '#F57C00' : '#E65100',
                         boxShadow: darkMode ? 
                           '0 6px 25px rgba(255, 152, 0, 0.4)' : 
                           '0 6px 25px rgba(245, 124, 0, 0.4)',
                         transform: 'translateY(-1px)'
                       },
                       transition: 'all 0.3s ease'
                     }}
                   >
                    <LogOut size={20} style={{ marginRight: '8px' }} />
                    Выйти сейчас
                  </IconButton>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default AuthErrorPage;
